import { Spinner } from "@/components/ui/v2/spinner";
import { companyTimeAttendanceApi } from "@/services/(company)/api.time-attendance";
import { store } from "@/store";
import {
  CompanyApplyOvertime,
  CompanyAttendanceExceptionView,
  CompanyAttendanceExceptionsList,
  CompanyEmployeeDrillDownLayout,
  CompanyOvertimeDetail,
  CompanyOvertimeList,
  CompanyRegularizeRequestsList,
  CompanyTimeAttendanceLayout,
  CompanyTimeAttendanceList,
  CompanyUpsertRegularization,
  CompanyViewRegularization,
  CompanyTimeAttendanceSettingsPage,
} from "@/utils/lazyLoadComponents";
import type { RouteObject } from "react-router-dom";
import { redirect } from "react-router-dom";
import { addSlashes, withCompanyPrefix } from "../route.utils";
import { MODULE_SETTINGS_PATHS } from "./module-settings.paths";

export const TIME_ATTENDANCE_DYNAMIC_ID_KEY = "id";

export enum COMPANY_TIME_ATTENDANCE_PATH_SEGMENTS {
  ROOT = "time-attendance",
  OVERVIEW = "overview",
  REGULARIZATION = "regularization",
  ADD = "add",
  EDIT = "edit",
  OVERTIME = "overtime",
  ATTENDANCE_EXCEPTIONS = "attendance-exceptions",
  DETAILS = `:${TIME_ATTENDANCE_DYNAMIC_ID_KEY}`,
  // Module settings (moved out of Company Settings — also absorbs the old
  // Shift Settings tab and the standalone Overtime Policy route)
  // Literal, not `MODULE_SETTINGS_SEGMENT`: `isolatedModules` forbids enum
  // members initialized from an imported const. Keep both in sync.
  SETTINGS = "settings",
}

export const companyTimeAttendanceRoutes: RouteObject = {
  path: COMPANY_TIME_ATTENDANCE_PATH_SEGMENTS.ROOT,
  handle: { breadcrumb: "Time & Attendance" },
  HydrateFallback: Spinner,
  // No Component here — the page-level tabs layout and the :id drill-down are
  // now independent siblings (the drill-down no longer shows the tab strip).
  children: [
    // ── Page-level tabs layout: each tab owns its own stats + table ──────────
    {
      Component: CompanyTimeAttendanceLayout,
      handle: { flushContent: true },
      children: [
        {
          index: true,
          loader: () => redirect(COMPANY_TIME_ATTENDANCE_PATHS.OVERVIEW),
        },
        {
          path: COMPANY_TIME_ATTENDANCE_PATH_SEGMENTS.OVERVIEW,
          handle: { breadcrumb: "Overview" },
          Component: CompanyTimeAttendanceList,
        },
        {
          path: COMPANY_TIME_ATTENDANCE_PATH_SEGMENTS.REGULARIZATION,
          handle: { breadcrumb: "Regularization" },
          Component: CompanyRegularizeRequestsList,
        },
        {
          path: COMPANY_TIME_ATTENDANCE_PATH_SEGMENTS.OVERTIME,
          handle: { breadcrumb: "Overtime" },
          Component: CompanyOvertimeList,
        },
        {
          path: COMPANY_TIME_ATTENDANCE_PATH_SEGMENTS.ATTENDANCE_EXCEPTIONS,
          handle: { breadcrumb: "Attendance Exceptions" },
          Component: CompanyAttendanceExceptionsList,
        },
      ],
    },

    // ── Employee drill-down: standalone page (own stats + own 403 guard) ─────
    // ── Module settings — a SIBLING of the tabs layout, never a child of it:
    // nesting it inside would render it under a tab bar with no matching tab.
    // Static "settings" also outranks the dynamic siblings in React Router's
    // route ranking, so there is no collision. ──────────────────────────────
    {
      path: COMPANY_TIME_ATTENDANCE_PATH_SEGMENTS.SETTINGS,
      handle: { breadcrumb: "Settings", title: "Time & Attendance Settings" },
      Component: CompanyTimeAttendanceSettingsPage,
    },
    {
      path: COMPANY_TIME_ATTENDANCE_PATH_SEGMENTS.DETAILS,
      loader: async ({ params }) => {
        const id = Number(params[TIME_ATTENDANCE_DYNAMIC_ID_KEY]);
        const result = await store.dispatch(
          companyTimeAttendanceApi.endpoints.getCompanyEmployeeTimeAttendanceSummary.initiate(
            { userId: id },
          ),
        );
        const name = result.data?.data?.user?.name ?? `Employee #${id}`;
        return { breadcrumbLabel: name };
      },
      handle: {
        breadcrumb: (data: { breadcrumbLabel: string }) =>
          data?.breadcrumbLabel ?? "Employee",
        title: "Time Attendance",
      },
      Component: CompanyEmployeeDrillDownLayout,
    },

    // ── Regularization detail/form routes (no stats, no tabs) ──────────────
    {
      path: COMPANY_TIME_ATTENDANCE_PATH_SEGMENTS.REGULARIZATION,
      handle: { breadcrumb: "Regularization" },
      children: [
        {
          path: COMPANY_TIME_ATTENDANCE_PATH_SEGMENTS.ADD,
          handle: { breadcrumb: "Add" },
          Component: CompanyUpsertRegularization,
        },
        {
          path: COMPANY_TIME_ATTENDANCE_PATH_SEGMENTS.DETAILS,
          loader: ({ params }) => ({
            breadcrumbLabel: `Employee #${params[TIME_ATTENDANCE_DYNAMIC_ID_KEY]}`,
          }),
          handle: {
            breadcrumb: (data: { breadcrumbLabel: string }) =>
              data?.breadcrumbLabel ?? "Employee",
          },
          Component: CompanyViewRegularization,
        },
        {
          path: `${COMPANY_TIME_ATTENDANCE_PATH_SEGMENTS.DETAILS}/${COMPANY_TIME_ATTENDANCE_PATH_SEGMENTS.EDIT}`,
          handle: { breadcrumb: "Edit" },
          Component: CompanyUpsertRegularization,
        },
      ],
    },

    // ── Overtime detail/form routes (no stats, no tabs) ────────────────────
    {
      path: COMPANY_TIME_ATTENDANCE_PATH_SEGMENTS.OVERTIME,
      handle: { breadcrumb: "Overtime" },
      children: [
        {
          path: COMPANY_TIME_ATTENDANCE_PATH_SEGMENTS.ADD,
          handle: { breadcrumb: "Create Request" },
          Component: CompanyApplyOvertime,
        },
        {
          path: COMPANY_TIME_ATTENDANCE_PATH_SEGMENTS.DETAILS,
          loader: ({ params }) => ({
            breadcrumbLabel: `Request #${params[TIME_ATTENDANCE_DYNAMIC_ID_KEY]}`,
          }),
          handle: {
            breadcrumb: (data: { breadcrumbLabel: string }) =>
              data?.breadcrumbLabel ?? "Request",
          },
          Component: CompanyOvertimeDetail,
        },
        {
          path: `${COMPANY_TIME_ATTENDANCE_PATH_SEGMENTS.DETAILS}/${COMPANY_TIME_ATTENDANCE_PATH_SEGMENTS.EDIT}`,
          handle: { breadcrumb: "Edit" },
          Component: CompanyOvertimeDetail,
        },
      ],
    },

    // ── Attendance exceptions detail routes (no stats, no tabs) ─────────────
    {
      path: COMPANY_TIME_ATTENDANCE_PATH_SEGMENTS.ATTENDANCE_EXCEPTIONS,
      handle: { breadcrumb: "Attendance Exceptions" },
      children: [
        {
          path: COMPANY_TIME_ATTENDANCE_PATH_SEGMENTS.DETAILS,
          handle: { breadcrumb: "Exception Details" },
          Component: CompanyAttendanceExceptionView,
        },
      ],
    },
  ],
};

export const COMPANY_TIME_ATTENDANCE_PATHS = Object.freeze({
  ROOT: withCompanyPrefix(
    addSlashes(COMPANY_TIME_ATTENDANCE_PATH_SEGMENTS.ROOT),
  ),

  // ── Module settings (resolved in module-settings.paths.ts) ────────────────
  SETTINGS: MODULE_SETTINGS_PATHS.TIME_ATTENDANCE.ROOT,
  SETTINGS_VTAB_QUICK_ACTIVITY: MODULE_SETTINGS_PATHS.TIME_ATTENDANCE.VTAB_QUICK_ACTIVITY,
  SETTINGS_VTAB_REMINDERS: MODULE_SETTINGS_PATHS.TIME_ATTENDANCE.VTAB_REMINDERS,
  SETTINGS_VTAB_RULES: MODULE_SETTINGS_PATHS.TIME_ATTENDANCE.VTAB_RULES,
  SETTINGS_VTAB_DEFAULT_RULE: MODULE_SETTINGS_PATHS.TIME_ATTENDANCE.VTAB_DEFAULT_RULE,
  SETTINGS_VTAB_SHIFTS: MODULE_SETTINGS_PATHS.TIME_ATTENDANCE.VTAB_SHIFTS,
  SETTINGS_VTAB_OVERTIME: MODULE_SETTINGS_PATHS.TIME_ATTENDANCE.VTAB_OVERTIME,

  OVERVIEW: withCompanyPrefix(
    addSlashes(
      COMPANY_TIME_ATTENDANCE_PATH_SEGMENTS.ROOT,
      COMPANY_TIME_ATTENDANCE_PATH_SEGMENTS.OVERVIEW,
    ),
  ),
  VIEW: (id: string | number) =>
    withCompanyPrefix(
      addSlashes(COMPANY_TIME_ATTENDANCE_PATH_SEGMENTS.ROOT, String(id)),
    ),
  VIEW_EMPLOYEE_OVERTIME: (id: string | number) =>
    withCompanyPrefix(
      addSlashes(
        COMPANY_TIME_ATTENDANCE_PATH_SEGMENTS.ROOT,
        String(id),
        COMPANY_TIME_ATTENDANCE_PATH_SEGMENTS.OVERTIME,
      ),
    ),
  REGULARIZATION: withCompanyPrefix(
    addSlashes(
      COMPANY_TIME_ATTENDANCE_PATH_SEGMENTS.ROOT,
      COMPANY_TIME_ATTENDANCE_PATH_SEGMENTS.REGULARIZATION,
    ),
  ),
  VIEW_REGULARIZATION: (id: string | number) =>
    withCompanyPrefix(
      addSlashes(
        COMPANY_TIME_ATTENDANCE_PATH_SEGMENTS.ROOT,
        COMPANY_TIME_ATTENDANCE_PATH_SEGMENTS.REGULARIZATION,
        String(id),
      ),
    ),
  ADD_REGULARIZATION: withCompanyPrefix(
    addSlashes(
      COMPANY_TIME_ATTENDANCE_PATH_SEGMENTS.ROOT,
      COMPANY_TIME_ATTENDANCE_PATH_SEGMENTS.REGULARIZATION,
      COMPANY_TIME_ATTENDANCE_PATH_SEGMENTS.ADD,
    ),
  ),
  EDIT_REGULARIZATION: (id: string | number) =>
    withCompanyPrefix(
      addSlashes(
        COMPANY_TIME_ATTENDANCE_PATH_SEGMENTS.ROOT,
        COMPANY_TIME_ATTENDANCE_PATH_SEGMENTS.REGULARIZATION,
        String(id),
        COMPANY_TIME_ATTENDANCE_PATH_SEGMENTS.EDIT,
      ),
    ),
  OVERTIME: withCompanyPrefix(
    addSlashes(
      COMPANY_TIME_ATTENDANCE_PATH_SEGMENTS.ROOT,
      COMPANY_TIME_ATTENDANCE_PATH_SEGMENTS.OVERTIME,
    ),
  ),
  ADD_OVERTIME: withCompanyPrefix(
    addSlashes(
      COMPANY_TIME_ATTENDANCE_PATH_SEGMENTS.ROOT,
      COMPANY_TIME_ATTENDANCE_PATH_SEGMENTS.OVERTIME,
      COMPANY_TIME_ATTENDANCE_PATH_SEGMENTS.ADD,
    ),
  ),
  VIEW_OVERTIME: (id: string | number) =>
    withCompanyPrefix(
      addSlashes(
        COMPANY_TIME_ATTENDANCE_PATH_SEGMENTS.ROOT,
        COMPANY_TIME_ATTENDANCE_PATH_SEGMENTS.OVERTIME,
        String(id),
      ),
    ),
  EDIT_OVERTIME: (id: string | number) =>
    withCompanyPrefix(
      addSlashes(
        COMPANY_TIME_ATTENDANCE_PATH_SEGMENTS.ROOT,
        COMPANY_TIME_ATTENDANCE_PATH_SEGMENTS.OVERTIME,
        String(id),
        COMPANY_TIME_ATTENDANCE_PATH_SEGMENTS.EDIT,
      ),
    ),
  ATTENDANCE_EXCEPTIONS: withCompanyPrefix(
    addSlashes(
      COMPANY_TIME_ATTENDANCE_PATH_SEGMENTS.ROOT,
      COMPANY_TIME_ATTENDANCE_PATH_SEGMENTS.ATTENDANCE_EXCEPTIONS,
    ),
  ),
  VIEW_EXCEPTION: (id: string | number) =>
    withCompanyPrefix(
      addSlashes(
        COMPANY_TIME_ATTENDANCE_PATH_SEGMENTS.ROOT,
        COMPANY_TIME_ATTENDANCE_PATH_SEGMENTS.ATTENDANCE_EXCEPTIONS,
        String(id),
      ),
    ),
});
